import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MsalService } from '@azure/msal-angular';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { LocalStorageService } from '../../../shared/storage/local-storage.service';
import { CustomPaginatorIntl } from './custom-paginator-intl';

@Component({
	selector: 'lvr-paginator',
	imports: [MatPaginatorModule],
	templateUrl: './paginator.html',
	styleUrl: './paginator.scss',
	providers: [{ provide: MatPaginatorIntl, useClass: CustomPaginatorIntl }],
})
export class Paginator {
	private readonly environment = inject(ENVIRONMENT);
	private readonly auth = inject(MsalService, { optional: true });
	private readonly storage = inject(LocalStorageService, { optional: true });

	public length = input.required<number>();
	public pageIndex = input<number>(0);
	public pageSize = input<number>(this.environment.defaultPageSize);
	public pageSizeOptions = input<number[]>(this.environment.pageSizeOptions);
	public disabled = input<boolean>(false);
	public showFirstLastButtons = input<boolean>(false);
	public hidePageSize = input<boolean>(false);
	public preferenceKey = input<string | undefined>(undefined);

	public page = output<PageEvent>();

	private readonly effectivePageSize = signal<number>(this.pageSize());

	public readonly totalItems = computed(() => this.length());
	public readonly currentPageIndex = computed(() => this.pageIndex());
	public readonly currentPageSize = computed(() => this.effectivePageSize());
	public readonly availablePageSizes = computed(() => this.pageSizeOptions());
	public readonly shouldBeDisabled = computed(() => this.disabled());
	public readonly shouldShowFirstLastButtons = computed(() => this.showFirstLastButtons());
	public readonly shouldHidePageSize = computed(() => this.hidePageSize());

	public constructor() {
		// Load saved page size preference if preferenceKey is provided
		effect(() => {
			const key = this.preferenceKey();
			const defaultSize = this.pageSize();
			const options = this.availablePageSizes();
			const userId = this.getCurrentUserId();

			if (key && userId && this.storage) {
				const savedPageSize = this.storage.getPageSize(userId, key);

				// Only use saved value if it's in the available options
				if (savedPageSize && options.includes(savedPageSize)) {
					this.effectivePageSize.set(savedPageSize);
					return;
				}
			}

			this.effectivePageSize.set(defaultSize);
		});
	}

	/**
	 * Emits the page change event and saves the page size preference.
	 * @param event The page event triggered.
	 */
	public emitPageChange(event: PageEvent) {
		const key = this.preferenceKey();
		const userId = this.getCurrentUserId();

		// Save page size preference if preferenceKey is provided and page size changed
		if (key && userId && this.storage && event.pageSize !== this.effectivePageSize()) {
			this.storage.setPageSize(userId, key, event.pageSize);
			this.effectivePageSize.set(event.pageSize);
		}

		this.page.emit(event);
	}

	private getCurrentUserId(): string | undefined {
		return this.storage?.resolveCurrentUserId(this.auth);
	}
}
